import { AuthProviderProps, hasAuthParams } from 'react-oidc-context';
import { ErrorResponse, Log, User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { isNullOrWhiteSpaces } from '../utils/strings';
import { clearRoleRedirects } from '../utils/redirect-guard';
import { matchesAnyPath } from '../utils/paths';

/**
 * Estos avisos son justamente los que uno quiere leer durante una incidencia, y el logger de
 * oidc-client-ts no escribe nada salvo que alguien haya llamado antes a `window.enableOidcLogs()`,
 * cosa que en prepro y produccion no pasa. Van por consola.
 */
const warn = (message: string, detail?: unknown) => console.warn(`auth-config: ${message}`, detail ?? '');

const POST_LOGIN_URL_PREFIX = 'post_login_url';

/**
 * En prepro y produccion las tres SPA cuelgan del mismo origen (`{BaseUrl}/identity`, `/website`,
 * `/backoffice`), asi que comparten un unico `localStorage`. Con una clave suelta, el destino que
 * guardaba el back office antes de mandarte al login se lo comia la web al volver. Se guarda por
 * cliente. En local no se notaba porque cada app vive en su puerto, y un puerto distinto es un
 * origen distinto.
 */
let postLoginUrlKey = POST_LOGIN_URL_PREFIX;

/**
 * `getOidcConfig` la fija, y las tres SPA la llaman en la evaluacion del modulo de su `index.tsx`,
 * antes de montar nada. Aun asi, si alguien llamase a `setPostLoginUrl` antes escribiria en la clave
 * heredada sin un solo aviso, y eso es indetectable mirando el codigo del sitio que falla.
 */
const assertPostLoginUrlScope = () => {
  if (postLoginUrlKey === POST_LOGIN_URL_PREFIX) {
    warn('se ha usado post_login_url antes de getOidcConfig, la clave no esta separada por cliente');
  }
};

/**
 * El prefijo con el que esta aplicacion cuelga del origen, el mismo que se le pasa al router. Lo
 * declara la aplicacion en `getOidcConfig`, porque lo que se lee de `window.location.pathname` lo lleva
 * delante y las rutas que se escriben en una lista no.
 */
let appBasename: string | undefined;

/**
 * Las pantallas que ESTABLECEN la sesion, tal y como las nombra cada aplicacion (sin el basename).
 *
 * Solo identity tiene mas de una; la web y el back office ya no tienen ninguna propia. Se declaran aqui
 * y no en el sitio que las usa porque hay dos sitios que las necesitan y no se hablan entre ellos:
 * `setPostLoginUrl`, para no guardar como destino la pantalla de la que se viene, y `session-guard`,
 * para no pedirle a alguien que vuelva a entrar mientras esta entrando.
 */
let appAuthPaths: string[] = [];

/** El basename declarado por la aplicacion, si lo declaro. */
export const getAppBasename = (): string | undefined => appBasename;

export const getOidcConfig = (options: {
  authority: string;
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  silentRedirectUri?: string;
  changePostLoginUrl?: (user: User, postLoginUrl: string) => string;
  scopes?: string[];
  /** El basename del router. Sin el, las rutas de `authPaths` se comparan contra la URL con el prefijo puesto y no casan. */
  basename?: string;
  /** Las pantallas de autenticacion de esta aplicacion, sin el basename. Ver `isAuthenticationUrl`. */
  authPaths?: string[];
  extraParams?: any;
}): AuthProviderProps => {
  postLoginUrlKey = isNullOrWhiteSpaces(options.clientId) ? POST_LOGIN_URL_PREFIX : `${POST_LOGIN_URL_PREFIX}:${options.clientId}`;
  appBasename = options.basename;
  appAuthPaths = options.authPaths ?? [];

  // Traspaso de una sola vez de la clave heredada. Todo el que haya entrado alguna vez tiene un
  // `post_login_url` suelto, y quien estuviera a mitad de login en el momento del despliegue
  // perderia su destino si nos limitasemos a dejar de leerlo. Despues se descarta, para que las tres
  // apps no se lo vuelvan a disputar en el origen compartido de prepro y prod.
  if (postLoginUrlKey !== POST_LOGIN_URL_PREFIX) {
    const inherited = localStorage.getItem(POST_LOGIN_URL_PREFIX);
    if (inherited != null) {
      if (localStorage.getItem(postLoginUrlKey) == null) localStorage.setItem(postLoginUrlKey, inherited);
      localStorage.removeItem(POST_LOGIN_URL_PREFIX);
    }
  }

  return {
    authority: options.authority,
    client_id: options.clientId,
    client_secret: options.clientSecret,
    redirect_uri: options.redirectUri,
    post_logout_redirect_uri: options.postLogoutRedirectUri ?? options.redirectUri,
    automaticSilentRenew: true,
    scope: [
      ...new Set(
        ['identity', 'website', options.clientId, 'openid', 'email', 'profile', ...(options?.scopes || [])].filter((x) => !isNullOrWhiteSpaces(x))
      )
    ].join(' '),
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    loadUserInfo: true,
    includeIdTokenInSilentRenew: true,
    includeIdTokenInSilentSignout: true,
    silent_redirect_uri: options.silentRedirectUri ?? options.redirectUri,
    onSigninCallback: (user) => {
      window.history.replaceState({}, document.title, window.location.pathname);
      if (user) {
        let postLoginUrl = getPostLoginUrl() ?? '';
        if (options?.changePostLoginUrl) postLoginUrl = options.changePostLoginUrl(user, postLoginUrl);
        if ((postLoginUrl || '').length > 0) {
          clearPostLoginUrl();
          window.location.href = postLoginUrl;
        }
      }
    },
    ...options.extraParams
  };
};

/**
 * El `UserManager` que usa `AuthProvider`, construido aqui en vez de dejarselo construir a el.
 *
 * Hace falta porque `session-guard` tiene que renovar por el manager DE VERDAD, no por el envoltorio
 * que `react-oidc-context` expone como `auth.signinSilent`. Ese envoltorio despacha `NAVIGATOR_INIT`
 * antes de esperar, o sea que pone `activeNavigator: 'signinSilent'`, y los layouts de las tres
 * aplicaciones reaccionan a ese estado pintando `.global-spinner`: un velo fijo de 100vh por 100vw
 * con «Refrescando credenciales». `SilentRenewService` llama al manager directamente, y por eso una
 * renovacion automatica no ha apagado nunca la pantalla. Renovando por el envoltorio, cada reintento
 * de fondo taparia la pagina encima del formulario que todo esto viene a proteger.
 *
 * Un unico manager, el mismo que `AuthProvider`, asi que sigue habiendo un solo `signinSilent` a la
 * vez sobre el `userStore`.
 */
let sharedUserManager: UserManager | null = null;

/** El manager compartido, si ya se ha construido. `session-guard` lo pide asi, sin la configuracion. */
export const peekSharedUserManager = (): UserManager | null => sharedUserManager;

/**
 * Las props para `<AuthProvider>`, con el manager compartido dentro.
 *
 * Se llama **despues** de `ensureFreshSession` y justo antes de renderizar: el constructor de
 * `UserManager` arranca ya la renovacion silenciosa si `automaticSilentRenew` esta puesto, y
 * adelantarlo solaparia esa renovacion con la comprobacion de arranque.
 *
 * `authority`, `client_id` y `redirect_uri` se quitan porque el tipo de `AuthProvider` los excluye
 * cuando se le pasa un manager: ya viven dentro de el.
 */
export const getAuthProviderProps = (oidcConfig: AuthProviderProps): AuthProviderProps => {
  if (sharedUserManager == null) sharedUserManager = new UserManager(oidcConfig as UserManagerSettings);

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const { authority, client_id, redirect_uri, ...rest } = oidcConfig as AuthProviderProps & UserManagerSettings;
  /* eslint-enable @typescript-eslint/no-unused-vars */

  return { ...rest, userManager: sharedUserManager } as AuthProviderProps;
};

export const configOidcLogging = (debugAll?: boolean) => {
  if (!!debugAll || process.env.NODE_ENV === 'development') {
    Log.setLevel(Log.DEBUG);
    Log.setLogger(console);
  }
};

/**
 * Lo que hay que soltar al salir, por la puerta que sea. El destino guardado es del que se va, y la
 * cuenta de saltos vive en `sessionStorage` y sobrevive al viaje a IdentityServer y vuelta: sin
 * resetearla, quien sale desde la pantalla de rescate y vuelve a entrar rapido se encuentra otra vez
 * la misma pantalla, porque el contador sigue pasado dentro de su ventana.
 */
export const onLogout = () => {
  clearPostLoginUrl();
  clearRoleRedirects();
};

/**
 * La pantalla de login de identity, la unica que esta libreria conoce sin que se la declaren.
 *
 * Esta suelta, en una constante, porque la miran dos reglas distintas -- `shouldSkipCheck` e
 * `isAuthenticationUrl` -- y escrita dos veces acabarian divergiendo sin que nada lo dijera.
 */
const LOGIN_PATHNAME = /\/login\/?$/;

/**
 * Si esa URL es una pantalla que ESTABLECE la sesion: el login, el callback OIDC, un enlace de correo,
 * o cualquiera de las que la aplicacion haya declarado en `getOidcConfig({ authPaths })`.
 *
 * Existe porque hay dos sitios en los que confundir «entrar» con «haber caducado» hace dano:
 *
 * - `setPostLoginUrl`, que sin esto guarda como destino de despues del login **la propia pantalla de
 *   login**, y entonces entrar te devuelve a entrar (j2l-shared#124).
 * - `session-guard`, que sin esto pide volver a entrar a quien esta tecleando sus credenciales.
 *
 * No se usa `hasAuthParams()` porque esa mira siempre `window.location`, y aqui se clasifica una URL
 * cualquiera. Se le copia el criterio: `error` cuenta acompanado de `state` y no suelto, que es como
 * llega un callback. Suelto cazaria de rebote cualquier ruta de negocio que lleve un `?error=`.
 */
export const isAuthenticationUrl = (href?: string): boolean => {
  let url: URL;
  try {
    url = new URL(href ?? window.location.href, window.location.origin);
  } catch {
    // Una URL que no se puede leer no se puede descartar, y equivocarse hacia «no es de autenticacion»
    // es lo barato: como mucho se guarda un destino raro, que es lo que pasaba antes de todo esto.
    return false;
  }

  const query = url.searchParams;

  // Los mismos dos que `shouldSkipCheck`: `data` es una peticion de autorizacion en vuelo -- asi entrega
  // Duende su pantalla de login, con el `returnUrl` dentro -- y `code` es el del callback OIDC o el
  // token de un solo uso de un enlace de correo. Ninguno sobrevive a un viaje de ida y vuelta, asi que
  // tampoco sirven de destino.
  if (query.has('data') || query.has('code')) return true;
  if (query.has('error') && query.has('state')) return true;

  return LOGIN_PATHNAME.test(url.pathname) || matchesAnyPath(url.pathname, appAuthPaths, appBasename);
};

/** El destino guardado para despues del login, del cliente activo. */
export const getPostLoginUrl = (): string | null => {
  assertPostLoginUrlScope();
  return localStorage.getItem(postLoginUrlKey);
};

/**
 * Guarda a donde hay que volver despues del login. Sin argumento, la URL actual.
 *
 * **Rechaza los destinos de autenticacion.** El fallo reportado no era del guarda que llamaba a esto sin
 * argumento desde la pantalla de login, era que esta funcion aceptaba cualquier cosa: quien entraba
 * acababa de vuelta en la pantalla de entrar (j2l-shared#124). Un destino rechazado **no borra** el que
 * ya hubiera guardado: quien venia del dashboard y paso por el login conserva el suyo, que es bueno.
 */
export const setPostLoginUrl = (url?: string) => {
  assertPostLoginUrlScope();
  let value: string = url as string;

  if ((value || '').trim().length === 0) {
    const u = new URL(window.location.href);
    u.hash = '';
    value = u.href;
  }

  if (isAuthenticationUrl(value)) {
    warn('no se guarda una pantalla de autenticacion como destino de despues del login', value);
    return;
  }

  localStorage.setItem(postLoginUrlKey, value);
};

export const clearPostLoginUrl = () => {
  assertPostLoginUrlScope();
  localStorage.removeItem(postLoginUrlKey);
};

/**
 * En los cuatro desenlaces la aplicacion arranca con normalidad: ninguno pide al llamante que no
 * renderice. `'adopted'` si merece que el llamante lo mire, porque hay estado del navegador que no
 * es OIDC -- el del back office, por ejemplo -- y que no puede pasar de una persona a la siguiente.
 */
export type SessionCheckResult =
  /** No habia sesion cacheada, o no tocaba comprobar. Sigue el arranque normal. */
  | 'no-user'
  /** La sesion cacheada es la viva. Sigue el arranque normal. */
  | 'valid'
  /** La sesion viva es de otra persona y se ha adoptado. Sigue el arranque, ya como esa persona. */
  | 'adopted'
  /** Habia una sesion cacheada muerta y se ha borrado. Sigue el arranque, acabara en el login. */
  | 'cleared';

/**
 * Errores de autorizacion que significan «esta sesion ya no vale». Cualquier otro fallo -- un
 * timeout, un corte de red, identity reiniciandose a mitad de un despliegue -- significa «no he
 * podido preguntar», que no es lo mismo y no puede costarle la sesion a nadie.
 */
const SESSION_IS_DEAD_ERRORS = ['login_required', 'interaction_required', 'consent_required', 'account_selection_required'];

/**
 * Agnostico de version: react-oidc-context 3 no despacha el error original, sino
 * `{ ...normalizeError(error), source, args }`, con el error de verdad en `innerError`. En la 2 ese
 * campo no existe y la rama simplemente no encuentra nada, asi que el resultado es el mismo.
 *
 * El cast es a una forma local, no al tipo de la libreria, para que compile en ambas mayores.
 * `auth-config.agnostico.test.ts` lo fija: si se quita la rama de `innerError`, ese test se pone rojo.
 *
 * Esta exportado y no en linea dentro de `meansSessionIsDead` porque hay un segundo sitio que
 * clasifica por codigo: la mitad `invalid_grant` de `renewalIsHopeless`, en `session-recovery.ts`.
 * Cuando el desenvoltorio vivia dentro de `meansSessionIsDead`, las dos mitades del mismo predicado
 * discrepaban ante la misma entrada -- bajo react-oidc-context 3 un `login_required` se clasificaba y
 * un `invalid_grant` identico no -- y nada lo decia.
 */
export const oidcErrorCode = (error: unknown): string | null => {
  const candidate = error instanceof ErrorResponse ? error : (error as { innerError?: unknown } | null | undefined)?.innerError;
  return candidate instanceof ErrorResponse ? (candidate.error ?? null) : null;
};

export const meansSessionIsDead = (error: unknown): boolean => {
  const code = oidcErrorCode(error);
  return code != null && SESSION_IS_DEAD_ERRORS.includes(code);
};

/**
 * Techo del viaje al iframe. Muy por debajo de los 10 s que trae la libreria, porque esto bloquea el
 * primer render.
 */
const SILENT_CHECK_TIMEOUT_SECONDS = 3;

/**
 * Techo de la comprobacion **entera**, que no es lo mismo.
 *
 * `silentRequestTimeoutInSeconds` solo acota la navegacion del iframe. Lo que viene despues -- el
 * POST al endpoint de token y, como `getOidcConfig` pone `loadUserInfo: true`, el GET a userinfo --
 * no lleva techo ninguno. Y como los tres `index.tsx` esperan a esto antes de renderizar, un
 * IdentityServer que conteste al authorize pero se arrastre en el token dejaria las tres
 * aplicaciones en blanco, sin spinner y sin error, indefinidamente.
 */
const OVERALL_CHECK_TIMEOUT_MS = 6000;

/**
 * Paginas en las que no se comprueba nada, porque comprobar ahi hace dano.
 *
 * - El iframe de renovacion silenciosa arranca esta misma aplicacion, asi que preguntar otra vez
 *   seria recursivo.
 * - El callback OIDC trae `code` y `state`, y la sesion se esta estableciendo justo ahi.
 * - `/login` es la pantalla de login de identity, la que ESTABLECE la sesion. Correr la comprobacion
 *   ahi seria comprobar la sesion sobre la pagina que la crea. La web y el back office ya no tienen
 *   ninguna ruta `/login`, pero la regla se queda por identity, que si la tiene.
 * - Un `?data=` es una peticion de autorizacion en vuelo: asi es como Duende entrega su pantalla de
 *   login, con el `returnUrl` dentro. Cerrar sesion ahi mata una sesion buena a mitad del login y
 *   tira el destino que el usuario traia.
 *
 * **No mira las `authPaths` que declare la aplicacion**, y no es un olvido: `isAuthenticationUrl` si las
 * mira, y compartirla aqui ampliaria en silencio lo que se salta la comprobacion de arranque el dia que
 * identity declare las suyas. Ese es otro cambio, con otro riesgo, y no el que pide j2l-shared#124.
 */
const shouldSkipCheck = (): boolean => {
  if (window.self !== window.top || hasAuthParams()) return true;

  const query = new URLSearchParams(window.location.search);
  // `data` es una peticion de autorizacion en vuelo; `code` a secas es el token de un solo uso de un
  // enlace de correo -- restablecer contrasena, invitacion de usuario de empresa. Cerrar sesion en
  // cualquiera de las dos se lleva por delante el parametro, y el del correo ya no se puede recuperar:
  // hay que pedir otro email. El `code` del callback OIDC no se confunde con este, porque viaja con
  // `state` y `hasAuthParams()` ya lo ha atrapado arriba.
  if (query.has('data') || query.has('code')) return true;

  return LOGIN_PATHNAME.test(window.location.pathname);
};

/**
 * `isAborted` es lo que impide que la comprobacion perdedora del `Promise.race` haga dano. Cuando
 * vence el plazo, la aplicacion ya ha arrancado y `AuthProvider` esta montado sobre el mismo
 * `userStore`: sin esto, una respuesta que llegase tarde podria escribir el token de otra persona,
 * vaciar el store bajo una aplicacion en marcha, o peor, lanzar una navegacion de pagina completa y
 * llevarse por delante lo que el usuario tuviera a medias.
 */
const checkSession = async (oidcConfig: AuthProviderProps, isAborted: () => boolean): Promise<SessionCheckResult> => {
  if (shouldSkipCheck()) return 'no-user';

  const userManager = new UserManager({
    ...(oidcConfig as UserManagerSettings),
    // No dejar montado un segundo temporizador de renovacion al lado del que crea `AuthProvider`.
    automaticSilentRenew: false,
    // Las dos siguientes son las que hacen que esto sirva de algo. Con los valores por defecto, el
    // servidor rechaza por `id_token_hint` y la libreria vuelve a rechazar por su cuenta al ver un
    // `sub` distinto, asi que «no hay sesion» y «la sesion es de otra persona» llegan aqui como el
    // mismo error y son indistinguibles. Apagadas las dos, la respuesta trae al usuario que de
    // verdad tiene la sesion y decide la comparacion de `sub` de mas abajo, que es donde queremos
    // que se decida.
    includeIdTokenInSilentRenew: false,
    validateSubOnSilentRenew: false,
    silentRequestTimeoutInSeconds: SILENT_CHECK_TIMEOUT_SECONDS
  });

  try {
    const stored = await userManager.getUser();
    if (stored == null) return 'no-user';

    // `signinSilent` hace corto-circuito con el grant de refresh cuando el usuario guardado trae
    // `refresh_token`, y en ese camino no habla con la sesion de IdentityServer para nada:
    // devolveria el mismo `sub` rancio y diriamos «valida». Hoy no ocurre porque ninguna SPA pide
    // `offline_access`, pero los cuatro clientes estan sembrados con `AllowOfflineAccess = true` y
    // `getOidcConfig` acepta `scopes` abierto, asi que anadirlo desactivaria este arreglo entero sin
    // que nada fallase. Mejor no responder que responder que si.
    if (!isNullOrWhiteSpaces(stored.refresh_token)) {
      warn('hay refresh_token, la sesion no se puede verificar por signinSilent');
      return 'no-user';
    }

    if (isAborted()) return 'no-user';

    let live: User | null = null;
    try {
      live = await userManager.signinSilent();
    } catch (error) {
      if (!meansSessionIsDead(error)) {
        // No se ha podido preguntar. Se deja todo como estaba: convertir un hipo de red en un
        // logout seria mucho peor que el desfase que esto evita.
        warn('no se ha podido comprobar la sesion, se deja el usuario como estaba', error);
        return 'no-user';
      }
      live = null;
    }

    // Desde aqui todo son efectos: si ya se ha arrancado sin nosotros, no se toca nada.
    if (isAborted()) return 'no-user';

    if (live?.profile?.sub != null && live.profile.sub === stored.profile?.sub) return 'valid';

    clearPostLoginUrl();
    clearRoleRedirects();

    if (live != null) {
      // La sesion viva es de otra persona, y para llegar aqui esa persona ha tenido que autenticarse:
      // no hay ningun camino en que la sesion viva cambie sin que alguien meta sus credenciales. O
      // sea que esto no es adoptar a escondidas, es reconocer un login que acaba de ocurrir.
      //
      // `signinSilent` ya ha guardado a esa persona en el store al construirla, asi que la aplicacion
      // arranca siendo quien de verdad tiene la sesion y no hay nada mas que hacer.
      //
      // Aqui hubo un cierre de sesion completo, para «no adoptar al usuario vivo en silencio». Se
      // quito porque costaba un login de mas y no protegia de nada: el caso que preocupaba -- llegar
      // y encontrarte la sesion de otro -- es `guardado == vivo`, donde esta rama ni se ejecuta.
      return 'adopted';
    }

    await userManager.removeUser();
    await userManager.clearStaleState();
    return 'cleared';
  } finally {
    // El manager es de usar y tirar, pero `signinSilent` arma los temporizadores de
    // `AccessTokenEvents` sobre el. Sin esto siguen corriendo toda la vida de la pagina.
    //
    // Agnostico de version: en oidc-client-ts 2 `unload()` devuelve `void` y en la 3 `Promise<void>`.
    // `Promise.resolve` normaliza las dos, y el `.catch` se traga un fallo de limpieza que no puede
    // cambiar el desenlace que la funcion ya ha decidido. Sin esperarla, en la 3 quedaria una promesa
    // colgando dentro de un `finally`: si fallara saldria como unhandled rejection sin decir nada util.
    await Promise.resolve(userManager.events.unload()).catch(() => {});
  }
};

/**
 * Contrasta el usuario que hay en `localStorage` con la sesion viva de IdentityServer, antes de que
 * la aplicacion llegue a creerselo.
 *
 * Hace falta porque `signoutRedirect()` solo limpia la aplicacion desde la que sales: las otras dos
 * se quedan con su copia del usuario anterior y con un access token bueno durante una hora
 * (`AccessTokenLifetime = 3600`, y ningun cliente tiene front-channel logout). Ese desfase es lo
 * que hacia que la web mandase al back office por un rol caducado mientras el back office devolvia
 * a la web por el rol nuevo, sin fin (issue 825).
 *
 * Vigilar la sesion con un iframe no serviria: el fallo ocurre en carga en frio, con la pestana de
 * la otra aplicacion cerrada. Hay que preguntar al arrancar.
 *
 * `signinSilent()` ya viaja con `id_token_hint`, porque `getOidcConfig` pone
 * `includeIdTokenInSilentRenew: true`, asi que IdentityServer responde `login_required` cuando la
 * sesion viva es de otra persona, sin llegar a emitir un token para ella. Aun asi se compara el
 * `sub` despues: si alguna version del servidor decidiese devolver el usuario vivo en vez del
 * error, el desenlace tiene que ser el mismo.
 *
 * Se llama **una sola vez, esperada y antes de montar `AuthProvider`**. Metida en un `useEffect`
 * habria dos `UserManager` haciendo `signinSilent` a la vez sobre el mismo `userStore`.
 *
 * Cuando termina, la aplicacion arranca siempre: o con la sesion que ya tenia, o con la de quien de
 * verdad esta dentro, o sin ninguna y camino del login. Ningun desenlace deja la pagina en blanco.
 *
 * Nunca lanza. Si la comprobacion se rompe se deja arrancar: dejar la aplicacion inservible por no
 * poder preguntar seria peor que el desfase que esto evita.
 */
export const ensureFreshSession = async (oidcConfig: AuthProviderProps): Promise<SessionCheckResult> => {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const deadline = new Promise<SessionCheckResult>((resolve) => {
    timer = setTimeout(() => {
      warn(`la comprobacion de sesion ha pasado de ${OVERALL_CHECK_TIMEOUT_MS} ms, se arranca sin ella`);
      resolve('no-user');
    }, OVERALL_CHECK_TIMEOUT_MS);
  });

  let aborted = false;

  try {
    return await Promise.race([checkSession(oidcConfig, () => aborted), deadline]);
  } catch {
    return 'no-user';
  } finally {
    // Gane quien gane, a partir de aqui la comprobacion no puede tocar el store ni navegar.
    aborted = true;
    if (timer != null) clearTimeout(timer);
  }
};

/**
 * Recarga la pestana cuando otra cambia de usuario.
 *
 * Hace falta porque el interceptor de axios no lee el token del contexto de React, sino de
 * `localStorage` (`axios-config.ts`), que es la misma clave que se reescribe al adoptar una sesion
 * ajena. Sin esto, una pestana abierta con la interfaz de A empezaria a llamar a la API con el token
 * de B: la pantalla dice una cosa y el servidor hace otra, y un clic ahi escribe en la cuenta
 * equivocada.
 *
 * Solo se recarga si cambia el `sub`. La renovacion silenciosa reescribe esa misma clave cada hora
 * larga con la misma persona dentro, y recargar por eso seria tirarle la pagina al usuario sin
 * motivo. El evento `storage` solo llega a las OTRAS pestanas, que es justo a quienes hay que avisar.
 */
export const watchSessionChanges = (oidcConfig: AuthProviderProps): void => {
  // Agnostico de version: `AuthProviderProps` es una union en las dos, pero en la 2 la rama sin user
  // manager declara `authority?: never` y en la 3 esa salida desaparece, asi que leerlos directamente
  // de la union solo compila en la 2. Mismo ensanchamiento que ya usa `getAuthProviderProps`.
  const { authority, client_id } = oidcConfig as AuthProviderProps & UserManagerSettings;
  const key = `oidc.user:${authority}:${client_id}`;

  const subDe = (raw: string | null): string | null => {
    try {
      return raw == null ? null : (JSON.parse(raw)?.profile?.sub ?? null);
    } catch {
      return null;
    }
  };

  window.addEventListener('storage', (event) => {
    if (event.key !== key) return;
    if (subDe(event.oldValue) === subDe(event.newValue)) return;
    warn('la sesion ha cambiado de persona en otra pestana, se recarga esta');
    window.location.reload();
  });
};

if (window) (window as any)['enableOidcLogs'] = () => configOidcLogging(true);
